import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/admin/AdminResetPasswordConfirmPage.css';
import AuthContext from '../../components/AuthProvider';
import AdminResetPasswordForm from '../../forms/admin/AdminLoginForm';
import { checkUserPasswordResetToken, resetUserPassword } from '../../services/UserAuthService';

const AdminResetPasswordConfirmPage = () => {
  const navigate = useNavigate();
  const { getCookie } = useContext(AuthContext);
  const { uidb64, token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(true);

  const MAX_PASSWORD_LENGTH = 128;
  const MIN_PASSWORD_LENGTH = 8;

  useEffect(() => {
    const validateToken = async () => {
      setLoading(true);
      try {
        const result = await checkUserPasswordResetToken(uidb64, token);
        setTokenValid(result.success);
        setErrorMessage(result.message || 'An unexpected error occurred.');
      } catch (error) {
        setErrorMessage('Error: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [uidb64, token]);

  const handlePasswordChange = (e) => setPassword(e.target.value);
  const handleConfirmPasswordChange = (e) => setConfirmPassword(e.target.value);

  const validatePassword = () => {
    if (!password || !confirmPassword) {
      return 'All fields are required.';
    } else if (password.length < MIN_PASSWORD_LENGTH) {
      return `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`;
    } else if (password.length > MAX_PASSWORD_LENGTH) {
      return `Password cannot exceed ${MAX_PASSWORD_LENGTH} characters.`;
    } else if (password !== confirmPassword) {
      return 'Passwords do not match.';
    }
    return '';
  };

  const handleSubmit = async () => {
    setFormSubmitted(true);
    const validationError = validatePassword();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setLoading(true);
    try {
      const csrftoken = getCookie('csrftoken');
      const result = await resetUserPassword(uidb64, token, password, csrftoken);
      setSuccessMessage(result.message);
      if (result.success) {
        setShowPasswordFields(false);
      } else {
        setErrorMessage(result.message);
      }
    } catch (error) {
      setErrorMessage('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin__reset-password-page">
      <div className="admin__modal">
        <div className="admin__modal-content">
          <div className="admin__modal-intro">
            <h2>Reset Password</h2>
            <p>Enter a new password to access your account.</p>
          </div>
          {tokenValid && showPasswordFields && (
            <AdminResetPasswordForm
              password={password}
              confirmPassword={confirmPassword}
              errorMessage={errorMessage}
              successMessage={successMessage}
              loading={loading}
              formSubmitted={formSubmitted}
              handlePasswordChange={handlePasswordChange}
              handleConfirmPasswordChange={handleConfirmPasswordChange}
            />
          )}
          {!showPasswordFields && successMessage && (
            <div className="success-message-container">
              <p className="success-message">{successMessage}</p>
            </div>
          )}
          <div className="admin__modal-buttons">
            {showPasswordFields ? (
              <>
                <button
                  className="button secondary"
                  onClick={() => navigate('/')}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="button primary"
                  onClick={handleSubmit}
                  disabled={loading || !password || !confirmPassword || !!successMessage}
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <button
                className="button primary-btn"
                onClick={() => navigate('/admin/login')}
              >
                Go to Login
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminResetPasswordConfirmPage;